import CustomRadioGroup from "@/components/inputs/custom-radio-group";
import CustomTextArea from "@/components/inputs/custom-textarea";
import type { InputCardOption } from "@/components/inputs/types";
import { Button } from "@/components/ui/button";
import { FieldLegend, FieldSet } from "@/components/ui/field";
import { useSubmitVouch } from "@/hooks/use-submit-vouch";
import {
  countVouchLetters,
  DURATION_OPTIONS,
  MIN_VOUCH_LETTERS,
  RELATIONSHIP_OPTIONS,
} from "@/lib/constants";
import type { ConditionalQuestion } from "@/lib/vouch-flow";
import { useNavigate } from "react-router";

interface VouchingFormProps {
  voucheeName?: string;
}

const QUESTION_FIELDS: Partial<
  Record<
    ConditionalQuestion,
    { legend: (voucheeName?: string) => string; options: InputCardOption[] }
  >
> = {
  Duration: {
    legend: (voucheeName) => `How long have you known ${voucheeName ?? ""}?`,
    options: DURATION_OPTIONS,
  },
};

// This form should contain questions about how the person filling
// vouch is related to the person they are vouching for
export const VouchingForm = ({ voucheeName }: VouchingFormProps) => {
  const navigate = useNavigate();
  const {
    relationship,
    duration,
    comments,
    fieldError,
    visibleQuestions,
    showComments,
    updateMeta,
    handleContinue,
    isDisabled,
  } = useSubmitVouch();

  const commentsLetterCount = countVouchLetters(comments);
  const shouldHideCounter = commentsLetterCount >= MIN_VOUCH_LETTERS;

  const answers: Partial<Record<ConditionalQuestion, string>> = {
    Duration: duration,
  };

  return (
    <form className="flex w-full flex-col gap-6">
      <FieldSet className="w-full">
        <FieldLegend>How do you know {voucheeName}?</FieldLegend>
        <div className="w-full">
          <CustomRadioGroup
            name="Relationship"
            options={RELATIONSHIP_OPTIONS}
            className="grid grid-cols-1 gap-2 sm:grid-cols-2"
            value={relationship}
            onValueChange={(val) => updateMeta({ Relationship: val as string })}
          />
        </div>
      </FieldSet>

      {visibleQuestions.map((question) => {
        const field = QUESTION_FIELDS[question];
        if (!field) return null;
        return (
          <FieldSet key={question} className="animate-reason w-full">
            <FieldLegend>{field.legend(voucheeName)}</FieldLegend>
            <div className="w-full">
              <CustomRadioGroup
                name={question}
                options={field.options}
                className="grid grid-cols-1 gap-2 sm:grid-cols-2"
                value={answers[question]}
                onValueChange={(val) =>
                  updateMeta({ [question]: val as string })
                }
              />
            </div>
          </FieldSet>
        );
      })}

      {showComments && (
        <FieldSet className="animate-reason w-full">
          <CustomTextArea
            label={`What would you want someone to know about ${voucheeName}?`}
            id="Comments"
            name="Comments"
            placeholder="Something a profile can't show."
            rows={3}
            className="bg-background"
            value={comments}
            onChange={(e) => updateMeta({ Comments: e.target.value })}
            error={fieldError.Comments}
            showCounter={!shouldHideCounter}
            count={commentsLetterCount}
            countLimit={MIN_VOUCH_LETTERS}
          />
        </FieldSet>
      )}

      {/*
      <FieldSet>
        <FieldLegend variant="label">
          Which of these best describe Sam?
        </FieldLegend>
        <FieldDescription>Choose any that apply</FieldDescription>
        <CustomCheckBoxGroup
          name="COMMENTS"
          options={COMMENTS_OPTIONS}
          className="grid sm:grid-cols-2 grid-cols-1 gap-4"
          // value={describe}
          // onValueChange={setDescribe}
        />
      </FieldSet> */}

      <div className="grid grid-cols-2 gap-2">
        <Button
          size="xl"
          type="button"
          variant="secondary"
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
        <Button
          size="xl"
          type="button"
          onClick={handleContinue}
          disabled={isDisabled}
        >
          Next
        </Button>
      </div>
    </form>
  );
};
